import { useMemo, useRef, useState } from 'react';
import { useContextMenu } from '~/systems/contextMenu';
import { clamp, useDOMNodeSize, useWindowInnerSize } from '~/util';
import { useWindowEvent, useWindowEventConditional } from '~/util/hooks';
import ContextMenuItem from '../ContextMenuItem/ContextMenuItem';
import type { MenuItem } from '~/systems/contextMenu';
import './ContextMenu.css';

export default function ContextMenu() {
  const menuSystem = useContextMenu();
  const rootNode = useRef<HTMLUListElement | null>(null);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [menuOpenPosition, setMenuOpenPosition] = useState({ x: 0, y: 0 });

  const rootNodeSize = useDOMNodeSize(rootNode);
  const innerSize = useWindowInnerSize();

  const menuEffectivePosition = useMemo(() => {
    let { x, y } = menuOpenPosition;
    const { width, height } = rootNodeSize;

    const maxX = innerSize.width - width;
    const maxY = innerSize.height - height;

    if (x > maxX && x >= width) {
      x -= width;
    }
    if (y > maxY && y >= height) {
      y -= height;
    }

    return {
      x: clamp(x, 0, maxX),
      y: clamp(y, 0, maxY)
    };
  }, [menuOpenPosition, rootNodeSize, innerSize]);

  let lastOpenTime = 0;
  const SECOND_OPEN_TIME_THRESHOLD = 300;

  const menuOpened = () => items.length > 0;

  useWindowEvent('contextmenu', (event) => {
    const now = Date.now();
    if (items.length === 0 && lastOpenTime + SECOND_OPEN_TIME_THRESHOLD < now) {
      const newItems = menuSystem.evaluateItems(event);
      if (newItems.length > 0) {
        lastOpenTime = now;
        setItems(newItems);
        setMenuOpenPosition({ x: event.clientX, y: event.clientY });
        event.preventDefault();
        event.stopPropagation();
      }
    } else {
      closeMenu();
    }
    menuSystem.clearOneShot();
  });

  useWindowEventConditional('keydown', menuOpened, (event) => {
    if (event.key === 'Escape') {
      closeMenu();
    }
  });

  useWindowEventConditional('pointerdown', menuOpened, (event) => {
    if (rootNode.current != null && event.target != null) {
      if (!rootNode.current.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
      }
    }
  });

  function closeMenu() {
    setItems([]);
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <ul
      ref={rootNode}
      className="ContextMenu u-scrollbarDark"
      style={{
        left: `${menuEffectivePosition.x}px`,
        top: `${menuEffectivePosition.y}px`
      }}
    >
      {items.map((item, idx) => (
        <ContextMenuItem key={idx} item={item} onClose={closeMenu} />
      ))}
    </ul>
  );
}
